import { LazyPreviewService } from 'src/app/services/lazy-preview.service';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  AfterViewChecked,
  ChangeDetectorRef,
  NgZone,
  HostListener,
  inject,
} from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { EMPTY, Subject, takeUntil } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { BackendApiService } from '../../../services/backend-api.service';
import { DialogService } from '../../../services/core/dialog.service';
import { ToasterService } from '../../../services/core/toaster.service';
import { UNSUPPORTED_FILE_PREVIEW_MESSAGE } from '../../../constants/app.constants';
import { DocumentService } from '../../document/document.service';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { DocumentImageAnnotatorComponent } from '../../document/document-image-annotator/document-image-annotator.component';
import { VideoPreviewComponent } from '../video-preview/video-preview.component';
import { AudioPreviewComponent } from '../audio-preview/audio-preview.component';
import { DocumentPreDefinedUploadFileComponent } from '../../document/document-pre-defined-upload-file/document-pre-defined-upload-file.component';
import { MediaUploadDialogComponent } from '../media-upload-dialog/media-upload-dialog.component';
import { ConfirmationPopupComponent } from '../confirmation-popup/confirmation-popup.component';
import { HelperService } from 'src/app/services/helper.service';
import { ALLOWED_UPLOAD_EXTENSIONS } from '../upload-file-types.constants';
import { DocumentCreateFolderComponent } from '../../document/document-create-folder/document-create-folder.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FileTypeService } from 'src/app/services/attachments/file-type.service';
import { ThumbnailGeneratorService } from 'src/app/services/attachments/thumbnail-generator.service';

/**
 * Document View Model Interface - Aligned with ProSuite patterns
 */
interface DocumentViewModel {
  // From backend API
  id: number | string;
  module_id: number;
  attachable_id?: number;
  /** Source delivery code for warehouse files mirrored from a delivery. */
  delivery_code?: string | null;
  original_name: string;
  file_name?: string;
  file_type: string;
  file_size: number;
  mime_type?: string;
  path?: string;
  url?: string;
  file_url?: string; // Direct file access URL from ProSuite
  thumbnail_url?: string;
  created_by?: any;
  created_at: string;
  updated_at: string;

  // ProSuite specific properties
  type?: 'file' | 'folder'; // Item type distinction
  is_virtual?: boolean; // Virtual folder support
  linked_module?: {
    // Cross-module linking
    id: number;
    name: string;
  };
  events?: any[]; // Document events for audit trail
  children?: any[]; // Child folders for hierarchy

  // Client-side enhancements
  displaySize: string;
  typeIcon: string;
  typeColor: string;
  isSelected: boolean;
  isStarred: boolean;
  isPreviewable: boolean;
  formattedDate: string;
  ownerName: string;
  ownerAvatar: string;
  documentCategory:
    | 'contract'
    | 'invoice'
    | 'proposal'
    | 'receipt'
    | 'statement'
    | 'other';

  // UI State
  showActions?: boolean;
  isHovered?: boolean;
}

@Component({
  selector: 'app-module-documents',
  templateUrl: './module-documents.component.html',
  styleUrls: ['./module-documents.component.scss'],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ height: '0', opacity: 0 })),
      ]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),

    // panel enter from the top exactly as they do there.
    trigger('slideInFromTop', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate(
          '300ms ease-out',
          style({ transform: 'translateY(0)', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({ transform: 'translateY(-100%)', opacity: 0 }),
        ),
      ]),
    ]),
  ],
  standalone: false,
})
export class ModuleDocumentsComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit, AfterViewChecked
{
  private readonly lazyPreview = inject(LazyPreviewService);
  @Input() module_id: any;
  @Input() record_id: any;
  @Input() jobId?: number;
  @Input() customerId?: number;
  @Input() createPermission = 'DONOTAPPLY';
  @Input() createFolderPermission = 'DONOTAPPLY';
  @Input() sharePermission = 'DONOTAPPLY';
  @Input() downloadPermission = 'DONOTAPPLY';
  @Input() previewPermission = 'DONOTAPPLY';
  @Input() renamePermission = 'DONOTAPPLY';
  @Input() duplicatePermission = 'DONOTAPPLY';
  @Input() archivePermission = 'DONOTAPPLY';
  @Input() deletePermission = 'DONOTAPPLY';

  // Resolved permission flags. Computed ONCE (computeDocumentPermissions) because a
  // permission is role-based and identical for every folder/file — never recheck per
  // row or per change-detection cycle.
  docPerms = {
    upload: true,
    createFolder: true,
    share: true,
    download: true,
    preview: true,
    rename: true,
    duplicate: true,
    archive: true,
    delete: true,
  };
  @Input() viewType: 'main' | 'detail' = 'detail';
  @Input() isLocked: boolean = false;
  /** Warehouse-only: show a "Filter by delivery" dropdown driven by each
   *  file's source delivery_code. Default off (other modules unaffected). */
  @Input() enableDeliveryFilter: boolean = false;
  /** Optional fixed CSS height (e.g. '340px') for the document list/grid so
   *  the widget is a constant height and scrolls internally regardless of how
   *  many documents it holds. Null = sizes to content (default; other hosts
   *  unaffected). */
  @Input() maxContentHeight: string | null = null;
  /** Hides the "Documents / N documents · Last updated" header bar, along with
   *  the view-mode and collapse toggles it holds, so the panel starts at the
   *  search bar. Opt-in: hosts that don't set it keep the header (default off,
   *  other modules unaffected). */
  @Input() hideHeader: boolean = false;
  /** '' = all deliveries; otherwise a delivery_code to filter by. */
  selectedDeliveryCode: string = '';

  /**
   * Opt-in: when set (e.g. 'save_document'), the widget scopes itself to the
   * record's tagged event folder (resolved via getEventFolder, the reliable
   * lookup the gallery uses) instead of the `auto_parent` "first folder by
   * id" guess. Used by the warehouse Summary where auto_parent picks the
   * wrong root. Other hosts leave it unset → behaviour unchanged.
   */
  @Input() eventFolder?: string;
  /**
   *
   * Now defaults to true so every module using this shared widget gets the
   * feature (verified on Bid Summary first). Uploads remain gated by
   * `isLocked` and the existing create/upload permission via canDropFiles(),
   * so read-only or unpermitted contexts are unaffected. Set to false at a
   * call site to opt a specific module out.
   */
  @Input() enableDragDrop: boolean = true;
  /** Resolved id of `eventFolder`'s folder; null until resolved / when absent. */
  eventFolderId: number | null = null;

  /** Distinct source delivery codes among loaded documents (for the dropdown). */
  get deliveryFilterOptions(): string[] {
    const codes = (this.documents || [])
      .map((d) => d.delivery_code)
      .filter((c): c is string => typeof c === 'string' && c.length > 0);
    return Array.from(new Set(codes)).sort();
  }

  @Output() documentSelected = new EventEmitter<DocumentViewModel>();
  @Output() documentUploaded = new EventEmitter<DocumentViewModel>();

  // ViewChild references for folder scrolling
  @ViewChild('folderViewport') folderViewport?: ElementRef<HTMLElement>;
  @ViewChild('folderTabsContainer')
  folderTabsContainer?: ElementRef<HTMLElement>;

  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();
  private resizeObserver?: ResizeObserver;

  /** Pending out-of-zone scroll recalculation, cleared on destroy. */
  private scrollRecalcTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Bound handlers, held so removeEventListener can actually match them.
   * These were previously registered as inline arrow functions and "removed"
   * with a freshly created arrow — which removes nothing, so every instance of
   * this component (it appears on ten pages) leaked a document click listener
   * and a window resize listener, each retaining the whole component.
   */
  private readonly onDocumentClick = (): void => {
    this.closeContextMenu();
  };

  private readonly onWindowResize = (): void => {
    this.checkMobileView();
    this.calculateScrollDimensions();
  };
  private viewChecked = false;
  // Shown during the 5s initial delay (queue job warmup)
  isInitialPreparing: boolean = false;
  private readonly INITIAL_LOAD_DELAY_MS = 5000;
  private initialLoadTimer: ReturnType<typeof setTimeout> | null = null;

  // Document data
  documents: DocumentViewModel[] = [];
  private thumbnailCache = new Map<string, string>();
  private thumbnailsGenerating = new Set<string>();
  filteredDocuments: DocumentViewModel[] = [];
  sortedDocuments: DocumentViewModel[] = [];
  rootFolderId: number | null = null;
  currentFolderId: number | null = null;
  folderPath: any[] = [];

  // Folder navigation (similar to jobsite gallery)
  folders: Array<{ id: number; name: string; count: number }> = [
    { id: 0, name: 'All Documents', count: 0 },
  ];
  currentFolder: number = 0;
  navigationHistory: Array<{ id: number; name: string }> = [];
  firstLevelFolderIds: number[] = []; // Track first-level folder IDs to filter from "All Documents" view

  // Folder scrolling properties - NEW APPROACH
  currentScrollOffset: number = 0; // Current scroll position in pixels
  maxScrollOffset: number = 0; // Maximum scroll offset
  canScrollLeftState: boolean = false;
  canScrollRightState: boolean = false;
  isMobileView: boolean = false;
  private averageFolderWidth: number = 130; // Average width per folder for scroll amount

  // Context menu
  contextMenuVisible: boolean = false;
  contextMenuX: number = 0;
  contextMenuY: number = 0;
  contextMenuItem: DocumentViewModel | null = null;

  // Clipboard
  clipboardItem: DocumentViewModel | null = null;
  clipboardAction: 'copy' | 'cut' | null = null;
  clipboardItems: DocumentViewModel[] = []; // For bulk copy/cut operations

  // UI State
  searchQuery: string = '';
  activeFilter: string = 'all';
  isExpanded: boolean = true;
  viewMode: 'grid' | 'list' = 'list';
  selectedDocuments: string[] = [];
  sortBy: 'date' | 'name' | 'size' = 'date';
  sortOrder: 'asc' | 'desc' = 'desc';
  lastUpdated: Date = new Date();

  // Loading states
  isLoading: boolean = false;
  isSearching: boolean = false;
  isLoadingMore: boolean = false;
  isUploading: boolean = false;

  // Pagination
  currentPage: number = 1;
  totalItems: number = 0;
  itemsPerPage: number = 10;
  hasMore: boolean = true;
  showLoadMoreOption: boolean = false;

  // Document type mapping
  documentTypeConfig = {
    contract: {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      icon: 'text-purple-600',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-50',
      iconName: 'description',
    },
    invoice: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      icon: 'text-blue-600',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-50',
      iconName: 'receipt',
    },
    proposal: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      icon: 'text-orange-600',
      border: 'border-orange-200',
      hover: 'hover:bg-orange-50',
      iconName: 'assignment',
    },
    receipt: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      icon: 'text-green-600',
      border: 'border-green-200',
      hover: 'hover:bg-green-50',
      iconName: 'receipt_long',
    },
    statement: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      icon: 'text-yellow-600',
      border: 'border-yellow-200',
      hover: 'hover:bg-yellow-50',
      iconName: 'summarize',
    },
    other: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      icon: 'text-gray-600',
      border: 'border-gray-200',
      hover: 'hover:bg-gray-50',
      iconName: 'insert_drive_file',
    },
  };

  constructor(
    private backendApiService: BackendApiService,
    private documentService: DocumentService,
    private dialogService: NgDialogAnimationService,
    private toasterService: ToasterService,
    public dialog: NgDialogAnimationService,
    private cdr: ChangeDetectorRef,
    private helperService: HelperService,
    private zone: NgZone,
    private fileTypeService: FileTypeService,
    private thumbnailGeneratorService: ThumbnailGeneratorService,
  ) {}

  /**
   * Resolves every document permission a single time and caches the booleans.
   * Called from ngOnInit (and ngOnChanges when a permission input changes) so the
   * template binds plain fields instead of running permission checks per row/cycle.
   */
  private computeDocumentPermissions(): void {
    this.docPerms = {
      upload: this.helperService.isPermissionAvailable(this.createPermission),
      createFolder: this.helperService.isPermissionAvailable(
        this.createFolderPermission,
      ),
      share: this.helperService.isPermissionAvailable(this.sharePermission),
      download: this.helperService.isPermissionAvailable(
        this.downloadPermission,
      ),
      preview: this.helperService.isPermissionAvailable(this.previewPermission),
      rename: this.helperService.isPermissionAvailable(this.renamePermission),
      duplicate: this.helperService.isPermissionAvailable(
        this.duplicatePermission,
      ),
      archive: this.helperService.isPermissionAvailable(this.archivePermission),
      delete: this.helperService.isPermissionAvailable(this.deletePermission),
    };
  }

  private canCreateFolder(): boolean {
    return this.docPerms.createFolder;
  }
  private canShareDocument(): boolean {
    return this.docPerms.share;
  }
  private canDownloadDocument(): boolean {
    return this.docPerms.download;
  }
  private canPreviewDocument(): boolean {
    return this.docPerms.preview;
  }
  private canRenameDocument(): boolean {
    return this.docPerms.rename;
  }
  private canDuplicateDocument(): boolean {
    return this.docPerms.duplicate;
  }
  private canArchiveDocument(): boolean {
    return this.docPerms.archive;
  }
  private canDeleteDocument(): boolean {
    return this.docPerms.delete;
  }
  canUploadDocument(): boolean {
    return this.docPerms.upload;
  }

  ngOnInit(): void {
    this.computeDocumentPermissions();
    this.initializeSearch();
    // Only load documents if we have a valid record_id
    if (this.record_id) {
      // Show "Preparing..." during the 5s warmup, then switch to "Loading..." when the request fires
      this.isInitialPreparing = true;
      this.initialLoadTimer = setTimeout(() => {
        this.isInitialPreparing = false;
        if (this.eventFolder) {
          // Warehouse path: resolve the tagged folder directly (reliable),
          // bypassing the auto_parent "first folder by id" guess.
          this.resolveEventFolderThenLoad();
        } else {
          this.loadDocuments();
          this.fetchRootFolder();
        }
      }, this.INITIAL_LOAD_DELAY_MS);
    }
    // Add click listener to close context menu
    document.addEventListener('click', this.onDocumentClick);
    this.checkMobileView();
    // Add resize listener
    window.addEventListener('resize', this.onWindowResize);
  }

  ngAfterViewInit(): void {
    // Initial calculation after view is initialized
    setTimeout(() => {
      this.calculateScrollDimensions();
      this.setupResizeObserver();
    }, 100);
  }

  /**
   * Recalculate the folder-tab scroll metrics after the view settles.
   *
   * This used to schedule a plain setTimeout from inside the hook. The
   * `viewChecked` flag was meant to keep it to "once per change detection
   * cycle", but it could not: setTimeout is a zone macrotask, so when it fired
   * it triggered another ApplicationRef.tick(), whose ngAfterViewChecked found
   * the flag reset and scheduled the next one. The result was a perpetual
   * change-detection loop — measured at ~7.7 full passes/second while idle, each
   * one costing a whole-page CD pass plus a forced layout reflow (the
   * recalculation reads offsetWidth/scrollWidth). It ran on every page hosting
   * this component, which is ten of them.
   *
   * Now the work happens outside Angular, so it cannot schedule a tick, and we
   * only re-enter when a bound value actually changed — which converges after
   * one pass instead of never.
   */
  ngAfterViewChecked(): void {
    if (this.viewChecked || !this.folderViewport || !this.folderTabsContainer) {
      return;
    }
    this.viewChecked = true;

    this.zone.runOutsideAngular(() => {
      this.scrollRecalcTimer = setTimeout(() => {
        this.scrollRecalcTimer = null;
        const changed = this.calculateScrollDimensions();
        this.viewChecked = false;
        if (changed) {
          this.zone.run(() => this.cdr.markForCheck());
        }
      }, 0);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recompute cached permission flags only when a permission input actually changes.
    const permissionInputs = [
      'createPermission',
      'createFolderPermission',
      'sharePermission',
      'downloadPermission',
      'previewPermission',
      'renamePermission',
      'duplicatePermission',
      'archivePermission',
      'deletePermission',
    ];
    if (permissionInputs.some((key) => changes[key])) {
      this.computeDocumentPermissions();
    }

    // Check if record_id has changed and is now available
    if (changes['record_id']) {
      // ngOnInit handles the initial load with a delay — only react to subsequent record changes here
      if (changes['record_id'].isFirstChange()) return;

      const currentValue = changes['record_id'].currentValue;
      const previousValue = changes['record_id'].previousValue;

      // If record_id changed to a different valid value, reload immediately
      if (currentValue && currentValue !== previousValue) {
        console.log('Record ID changed to:', currentValue);
        this.loadDocuments();
        this.fetchRootFolder();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onWindowResize);

    if (this.scrollRecalcTimer) {
      clearTimeout(this.scrollRecalcTimer);
      this.scrollRecalcTimer = null;
    }

    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.initialLoadTimer) {
      clearTimeout(this.initialLoadTimer);
      this.initialLoadTimer = null;
      this.isInitialPreparing = false;
    }
  }

  private initializeSearch(): void {
    this.searchSubject$
      .pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.filterAndSortDocuments();
      });
  }

  /**
   * Resolve this record's tagged event folder (e.g. save_document) and scope
   * the listing + uploads to it — used when `eventFolder` is set so we don't
   * rely on the buggy auto_parent root pick. Falls back to an empty state if
   * the folder doesn't exist yet (e.g. nothing uploaded).
   */
  private resolveEventFolderThenLoad(): void {
    this.documentService
      .getEventFolder({
        module_id: this.module_id,
        attachable_id: this.record_id,
        event: this.eventFolder,
      })
      .subscribe(
        (response: any) => {
          const id =
            response?.data?.data?.id ||
            response?.data?.data?.data?.id ||
            response?.data?.folder_id ||
            response?.folder_id ||
            null;
          if (id) {
            this.eventFolderId = id;
            this.rootFolderId = id; // uploads from the widget go into this folder
          }
          this.loadDocuments();
        },
        () => {
          // No event folder yet → nothing to show. Avoid the wrong auto_parent fallback.
          this.eventFolderId = null;
          this.documents = [];
          this.filteredDocuments = [];
          this.sortedDocuments = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      );
  }

  loadDocuments(isLoadMore: boolean = false): void {
    console.log('record_id before loadDocuments', this.record_id);
    // Don't load if we don't have a valid record_id
    if (!this.record_id && !this.module_id) {
      console.log('Skipping document load - no valid record_id or module_id');
      return;
    }
    console.log('record_id after loadDocuments', this.record_id);

    if (this.isLoading && !isLoadMore) return;

    this.isLoading = !isLoadMore;
    this.isLoadingMore = isLoadMore;

    const queryParam: any = {
      module_id: this.module_id || 19, // Default to jobs module (19 for jobs, 13 for customer)
      attachable_id: this.record_id || this.jobId,
      page: this.currentPage,
      per_page: this.itemsPerPage,
      loadingService: false,
    };

    // Handle folder navigation
    if (this.currentFolder === 0) {
      if (this.eventFolderId) {
        // Warehouse path: scope to the tagged event folder's whole tree
        // (the reliable lookup) instead of the auto_parent "first folder by
        // id" guess that picks the wrong root on this data.
        queryParam.folder_id = this.eventFolderId;
        queryParam.all_files = true;
        queryParam.include = 'children_count,files_count';
      } else {
        // "All Documents" - use auto_parent and all_files without folder_id
        queryParam.auto_parent = true;
        queryParam.all_files = true;
        queryParam.include = 'children_count,files_count';
      }
    } else if (this.currentFolder > 0) {
      // Specific folder - add folder_id and include counts
      queryParam.folder_id = this.currentFolder;
      queryParam.include = 'children_count,files_count';
    } else if (this.currentFolderId) {
      // Legacy support for currentFolderId
      queryParam.folder_id = this.currentFolderId;
      queryParam.include = 'children_count,files_count';
    }

    this.documentService.getDocuments(queryParam).subscribe(
      (response: any) => {
        console.log('Document API Response:', response);

        // Handle ProSuite's nested response structure
        // API returns: response.data.data.documents.list
        let documentsArray = [];
        let pagination: any = {};

        if (response?.data?.data?.documents) {
          // ProSuite standard structure
          const documentsData = response.data.data.documents;
          documentsArray = documentsData.list || [];
          pagination = documentsData.pagination || {};
        } else if (response?.data && Array.isArray(response.data)) {
          // Fallback for direct data array
          documentsArray = response.data;
          pagination.total = response.meta?.total || documentsArray.length;
        } else if (Array.isArray(response)) {
          // Direct array response
          documentsArray = response;
          pagination.total = documentsArray.length;
        } else {
          console.warn('Unexpected response format:', response);
          documentsArray = [];
          pagination.total = 0;
        }

        let newDocuments = this.transformDocuments(documentsArray);

        // Handle initial load with auto_parent - populate folders if not yet populated
        if (
          !isLoadMore &&
          this.currentFolder === 0 &&
          queryParam.auto_parent &&
          this.folders.length === 1
        ) {
          const folderItems = documentsArray.filter(
            (item: any) => item.type === 'folder',
          );
          if (folderItems.length > 0) {
            this.firstLevelFolderIds = folderItems.map(
              (folder: any) => folder.id,
            );
            folderItems.forEach((folder: any) => {
              this.folders.push({
                id: folder.id,
                name: folder.original_name || folder.name || 'Untitled Folder',
                count: folder.children_count || folder.files_count || 0,
              });
            });
            console.log(
              'Initial folders populated from auto_parent:',
              this.folders,
            );
          }
        }

        // Filter out first-level folders when viewing "All Documents"
        if (this.currentFolder === 0 && this.firstLevelFolderIds.length > 0) {
          newDocuments = newDocuments.filter((doc) => {
            // Keep all files and folders that are NOT first-level folders
            return (
              doc.type !== 'folder' ||
              !this.firstLevelFolderIds.includes(Number(doc.id))
            );
          });
          console.log(
            'Filtered out first-level folders from All Documents view',
          );
        }

        if (isLoadMore) {
          this.documents = [...this.documents, ...newDocuments];
        } else {
          this.documents = newDocuments;
        }

        this.generateThumbnailsForDocuments(newDocuments);

        // Use pagination from ProSuite response
        this.totalItems = pagination.total || documentsArray.length;

        // Update "All Documents" count when all_files=true response is received
        if (this.currentFolder === 0 && queryParam.all_files) {
          const allDocsIndex = this.folders.findIndex((f) => f.id === 0);
          if (allDocsIndex !== -1) {
            this.folders[allDocsIndex].count =
              pagination.total || documentsArray.length;
          }
        }

        this.hasMore = this.documents.length < this.totalItems;
        this.showLoadMoreOption = this.hasMore;
        this.lastUpdated = new Date();

        this.filterAndSortDocuments();
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      (error: any) => {
        console.error('Error loading documents:', error);
        this.toasterService.showError('Failed to load documents');
        this.isLoading = false;
        this.isLoadingMore = false;
      },
    );
  }

  private transformDocuments(documents: any[]): DocumentViewModel[] {
    // Ensure we have a valid array
    if (!Array.isArray(documents)) {
      console.warn(
        'transformDocuments: Invalid input, expected array but got:',
        documents,
      );
      return [];
    }

    return documents.map((doc) => {
      // Handle both file and folder types from ProSuite
      const isFolder = doc.type === 'folder';
      const fileType = doc.file_type || (isFolder ? 'folder' : 'unknown');

      return {
        ...doc,
        // Ensure required fields have defaults
        id: doc.id || 0,
        module_id: doc.module_id || this.module_id || 19,
        original_name: doc.original_name || doc.name || 'Unnamed',
        file_type: fileType,
        file_size: doc.file_size || 0,
        created_at: doc.created_at || new Date().toISOString(),
        updated_at:
          doc.updated_at || doc.created_at || new Date().toISOString(),

        // ProSuite specific fields
        type: doc.type || 'file',
        is_virtual: doc.is_virtual || false,
        file_url: doc.file_url || doc.url,
        url: doc.url || doc.file_url, // Normalize to url field like Gallery widget
        thumbnail_url: doc.thumbnail_url || doc.url || doc.file_url,

        // Client-side enhancements
        displaySize: this.formatFileSize(doc.file_size || 0),
        typeIcon: isFolder ? 'folder' : this.getDocumentIcon(fileType),
        typeColor: isFolder ? 'folder' : this.getDocumentTypeColor(fileType),
        isSelected: false,
        isStarred: doc.is_starred || false,
        isPreviewable: !isFolder && this.isPreviewable(doc.mime_type),
        formattedDate: doc.created_a,
        ownerName: doc.creator?.full_name || 'System',
        ownerAvatar: this.getOwnerAvatar(doc.created_by),
        documentCategory: isFolder ? 'other' : this.categorizeDocument(doc),
        // Source delivery (warehouse files mirrored from a delivery); null
        // otherwise. Powers the optional "filter by delivery".
        delivery_code: doc.delivery_code || null,
      } as DocumentViewModel;
    });
  }

  /**
   * Generated preview for an already-uploaded PDF/video (page 1 render /
   * video frame, via the shared ThumbnailGeneratorService — images need
   * nothing extra, they're already their own thumbnail via `file_url`).
   */
  private async generateDocumentThumbnail(doc: DocumentViewModel): Promise<void> {
    const file_url = (doc as any).file_url;
    if (doc.type === 'folder' || !file_url) return;

    const cacheKey = String(doc.id);
    if (this.thumbnailCache.has(cacheKey) || this.thumbnailsGenerating.has(cacheKey)) {
      return;
    }

    this.thumbnailsGenerating.add(cacheKey);
    try {
      const thumbnail = await this.thumbnailGeneratorService.generateForUrl(
        file_url,
        doc.original_name,
        (doc as any).mime_type,
        doc.id, // lets Word files and spreadsheets be rendered (same input their previews use)
      );
      if (thumbnail) {
        this.thumbnailCache.set(cacheKey, thumbnail);
      }
    } catch (error) {
      console.error('Error generating thumbnail for document:', doc.id, error);
    } finally {
      this.thumbnailsGenerating.delete(cacheKey);
    }
  }

  /** Kicks off thumbnail generation for every document that supports one (PDFs, Word files, spreadsheets, HTML pages and videos). */
  private generateThumbnailsForDocuments(docs: DocumentViewModel[]): void {
    const needThumbnails = (docs || []).filter((doc) => {
      if (doc.type === 'folder' || !(doc as any).file_url) return false;
      const kind = this.fileTypeService.kindOf({
        original_name: doc.original_name,
        mime_type: (doc as any).mime_type,
      });
      return (
        kind === 'pdf' ||
        kind === 'word' ||
        kind === 'excel' ||
        kind === 'video' ||
        (kind === 'code' &&
          this.thumbnailGeneratorService.supportsClientThumbnail(doc.original_name, (doc as any).mime_type))
      );
    });

    for (const doc of needThumbnails) {
      this.generateDocumentThumbnail(doc);
    }
  }

  /**
   * `src` input for `app-file-thumbnail`: the generated client-side preview
   * when there is one, otherwise the file itself for images only — never the
   * raw PDF/other binary (that can't render in an `<img>` and would just be a
   * wasted failed request).
   */
  getThumbnailSrc(doc: DocumentViewModel): string | null {
    const cached = this.thumbnailCache.get(String(doc.id));
    if (cached) return cached;

    const kind = this.fileTypeService.kindOf({
      original_name: doc.original_name,
      mime_type: (doc as any).mime_type,
    });
    if (kind === 'image') return (doc as any).file_url || (doc as any).url || null;

    return null;
  }

  /** True once a real (generated) thumbnail is cached for this PDF/video. */
  hasGeneratedThumbnail(doc: DocumentViewModel): boolean {
    return this.thumbnailCache.has(String(doc.id));
  }

  private categorizeDocument(
    doc: any,
  ): 'contract' | 'invoice' | 'proposal' | 'receipt' | 'statement' | 'other' {
    const name = doc.original_name?.toLowerCase() || '';

    if (name.includes('contract') || name.includes('agreement'))
      return 'contract';
    if (name.includes('invoice') || name.includes('bill')) return 'invoice';
    if (name.includes('proposal') || name.includes('quote')) return 'proposal';
    if (name.includes('receipt')) return 'receipt';
    if (name.includes('statement')) return 'statement';

    return 'other';
  }

  onSearchChange(): void {
    this.searchSubject$.next(this.searchQuery);
  }

  setFilter(filter: string): void {
    this.activeFilter = filter;
    this.filterAndSortDocuments();
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'list' ? 'grid' : 'list';
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  toggleSort(field: 'date' | 'name' | 'size'): void {
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'desc';
    }
    this.filterAndSortDocuments();
  }

  /** Public hook for the delivery-filter dropdown (template-callable). */
  applyDeliveryFilter(): void {
    this.filterAndSortDocuments();
  }

  private filterAndSortDocuments(): void {
    // Filter documents
    this.filteredDocuments = this.documents.filter((doc) => {
      const matchesSearch = doc.original_name
        .toLowerCase()
        .includes(this.searchQuery.toLowerCase());
      const matchesFilter =
        this.activeFilter === 'all' ||
        doc.documentCategory === this.activeFilter;
      // Warehouse-only: filter by source delivery.
      const matchesDelivery =
        !this.enableDeliveryFilter ||
        !this.selectedDeliveryCode ||
        doc.delivery_code === this.selectedDeliveryCode;
      return matchesSearch && matchesFilter && matchesDelivery;
    });

    // Sort documents
    this.sortedDocuments = [...this.filteredDocuments].sort((a, b) => {
      let comparison = 0;

      if (this.sortBy === 'date') {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        comparison = dateA - dateB;
      } else if (this.sortBy === 'name') {
        comparison = a.original_name.localeCompare(b.original_name);
      } else if (this.sortBy === 'size') {
        comparison = a.file_size - b.file_size;
      }

      return this.sortOrder === 'asc' ? comparison : -comparison;
    });
  }

  toggleDocumentSelection(id: string): void {
    const index = this.selectedDocuments.indexOf(id);
    if (index > -1) {
      this.selectedDocuments.splice(index, 1);
    } else {
      this.selectedDocuments.push(id);
    }
  }

  isDocumentSelected(id: string): boolean {
    return this.selectedDocuments.includes(id);
  }

  clearSelection(): void {
    this.selectedDocuments = [];
  }

  // Favorite/Star functionality commented out
  /*
  toggleStar(id: string): void {
    const doc = this.documents.find(d => d.id.toString() === id);
    if (doc) {
      doc.isStarred = !doc.isStarred;
      // TODO: Update backend
      console.log(`Toggled star for document ${id}`);
    }
  }
  */

  handleBulkAction(action: string): void {
    if (this.isLocked) {
      return;
    }
    const selectedDocs = this.documents.filter((d) =>
      this.selectedDocuments.includes(d.id.toString()),
    );

    switch (action) {
      case 'download':
        if (!this.canDownloadDocument()) {
          return;
        }
        if (selectedDocs.length === 1) {
          this.downloadDocument(selectedDocs[0]);
        }
        break;
      case 'share':
        if (!this.canShareDocument()) {
          return;
        }
        if (selectedDocs.length === 1) {
          this.shareDocument(selectedDocs[0]);
        }
        break;
      case 'delete':
        if (!this.canDeleteDocument()) {
          return;
        }
        this.bulkDeleteDocuments(selectedDocs);
        break;
      default:
        console.log(
          `Performing ${action} on documents:`,
          this.selectedDocuments,
        );
    }
  }

  /**
   * Bulk delete selected documents
   */
  bulkDeleteDocuments(selectedDocs: DocumentViewModel[]): void {
    if (selectedDocs.length === 0) {
      return;
    }

    const itemText =
      selectedDocs.length === 1
        ? `"${selectedDocs[0].original_name}"`
        : `${selectedDocs.length} items`;

    const dialogRef = this.dialogService.open(ConfirmationPopupComponent, {
      data: {
        title: `Delete ${itemText}`,
        message: `Are you sure you want to delete ${itemText}?`,
        description: 'This action cannot be undone.',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        isDanger: true,
      },
      width: '500px',
      height: '300px',
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        // Use bulk delete API if multiple items
        if (selectedDocs.length > 1) {
          const bulkData = {
            folder_ids: selectedDocs.map((doc) => Number(doc.id)),
          };

          this.documentService.bulkDelete(bulkData).subscribe(
            (res: any) => {
              this.toasterService.showSuccessMessage(res?.data?.message);
              this.clearSelection();
              this.refreshCurrentView();
            },
            (error: any) => {
              console.error('Bulk delete error:', error);
              this.toasterService.showError('Failed to delete items');
            },
          );
        } else {
          // Single item delete (backward compatibility)
          this.documentService.deleteDocument(selectedDocs[0].id).subscribe(
            (res: any) => {
              this.toasterService.showSuccessMessage(res?.data?.message);
              this.clearSelection();
              this.refreshCurrentView();
            },
            (error: any) => {
              console.error('Delete error:', error);
              const itemType =
                selectedDocs[0].type === 'folder' ? 'folder' : 'document';
              this.toasterService.showError(`Failed to delete ${itemType}`);
            },
          );
        }
      }
    });
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.activeFilter = 'all';
    this.filterAndSortDocuments();
  }

  loadMore(): void {
    if (this.hasMore && !this.isLoadingMore) {
      this.currentPage++;
      this.loadDocuments(true);
    }
  }

  /**
  /**
   * ==========================================================================
   * DRAG & DROP UPLOAD (opt-in via `enableDragDrop`)
   * --------------------------------------------------------------------------
   * behaviour and UI match exactly: same validation, same queue, same 3-file
   * concurrency, same progress panel, same success/failure handling.
   * Only the upload context is adapted — module_id / record_id / folder_id come
   * from this widget, and refresh reuses refreshCurrentView().
   * ==========================================================================
   */

  /** True while a drag is over the widget; drives the overlay. */
  isDragging = false;
  /** Depth counter so the overlay does not flicker over child elements. */
  private dragCounter = 0;
  /** File count reported by the drag, shown in the overlay badge. */
  draggedFileCount = 0;

  /** Upload queue  */
  uploadQueue: any[] = [];
  /** Template alias for the queue, */
  uploadProgress: any[] = [];
  /** Whether the progress panel body is expanded. */
  isUploadPanelOpen = true;
  /** Active upload subscriptions, keyed by queue item id (for cancellation). */
  private currentUploads = new Map<string, any>();

  MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB — same as Task
  MAX_CONCURRENT_UPLOADS = 3; // same as Task
  ALLOWED_TYPES = ALLOWED_UPLOAD_EXTENSIONS;

  /** Drag & drop is only live when opted in and uploads are permitted. */
  canDropFiles(): boolean {
    return this.enableDragDrop && !this.isLocked && this.canUploadDocument();
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canDropFiles()) return;
    // No stopPropagation(): the event must reach window so the
    // window:drop / window:dragleave safety net below can fire.
    event.preventDefault();
    this.dragCounter++;
    this.isDragging = true;

    if (event.dataTransfer?.items) {
      this.draggedFileCount = event.dataTransfer.items.length;
    }
  }

  onDragOver(event: DragEvent): void {
    if (!this.canDropFiles()) return;
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    if (!this.canDropFiles()) return;
    event.preventDefault();
    this.dragCounter--;

    if (this.dragCounter <= 0) {
      this.clearDragState();
    }
  }

  onDrop(event: DragEvent): void {
    if (!this.canDropFiles()) return;
    event.preventDefault();

    // synchronously on drop. The overlay is independent of the upload queue and
    // of the document refresh — neither is allowed to gate it.
    this.isDragging = false;
    this.dragCounter = 0;
    this.draggedFileCount = 0;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files);
    }
  }

  /**
   * Safety net: clear the overlay on any drag that ends anywhere in the window.
   * Without this the overlay can stick when the browser swallows the final
   * dragleave, and a stuck overlay hides the refreshed list behind its blur.
   */
  @HostListener('window:drop')
  @HostListener('window:dragend')
  @HostListener('window:dragleave', ['$event'])
  onWindowDragEnd(event?: DragEvent): void {
    // window:dragleave fires constantly while dragging inside the page; only
    // treat it as "left the window" when the pointer actually exits.
    if (event && event.relatedTarget !== null) {
      return;
    }
    this.clearDragState();
  }

  /** Reset overlay state and force the view to repaint. */
  private clearDragState(): void {
    if (!this.isDragging && this.dragCounter === 0) return;
    this.isDragging = false;
    this.dragCounter = 0;
    this.draggedFileCount = 0;
    this.cdr.detectChanges();
  }

  /** Handle file upload with validation and queue management (same as Task). */
  handleFileUpload(files: FileList | File[]): void {
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      const validation = this.validateFile(file);
      if (!validation.valid) {
        this.toasterService.showError('', validation.error || 'Invalid file');
        continue;
      }

      if (this.isDuplicateFile(file)) {
        this.toasterService.showWarning(
          '',
          `${file.name} is already in upload queue`,
        );
        continue;
      }

      const queueItem: any = {
        id: this.generateUploadUUID(),
        file: file,
        fileName: file.name,
        fileSize: this.formatFileSize(file.size),
        fileSizeBytes: file.size,
        fileType: file.name.split('.').pop()?.toLowerCase() || '',
        status: 'pending',
        progress: 0,
        uploadedBytes: 0,
        totalBytes: file.size,
        preview: undefined,
        retryCount: 0,
      };

      this.uploadQueue.push(queueItem);

      // Thumbnail is generated AFTER queueing, never awaited before it.
      // Zone.js treats FileReader as a macrotask, so awaiting it here kept the
      // zone unstable — Angular could not run change detection, which left the
      // drag overlay painted on screen until the read finished.
      this.generatePreview(file).then((preview) => {
        queueItem.preview = preview;
      });
    }

    this.uploadProgress = this.uploadQueue;
    this.isUploadPanelOpen = true;

    this.processUploadQueue();
  }

  /** Validate file before upload — same rules as Task. */
  private validateFile(file: File): { valid: boolean; error?: string } {
    if (file.size > this.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds ${this.formatFileSize(this.MAX_FILE_SIZE)} limit`,
      };
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !this.ALLOWED_TYPES.includes(ext)) {
      return { valid: false, error: `File type .${ext} is not supported` };
    }

    return { valid: true };
  }

  /** Check if file already exists in upload queue — same rule as Task. */
  private isDuplicateFile(file: File): boolean {
    return this.uploadQueue.some(
      (item) =>
        item.fileName === file.name &&
        item.fileSizeBytes === file.size &&
        item.status !== 'error' &&
        item.status !== 'cancelled',
    );
  }

  /** Image thumbnail  */
  private generatePreview(file: File): Promise<string | undefined> {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(undefined);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(file);
    });
  }

  private generateUploadUUID(): string {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  // Note: formatFileSize() already exists on this component (private, further
  // down) and is reused here rather than redeclared.

  formatUploadSpeed(bytesPerSecond: number): string {
    if (!bytesPerSecond) return '0 KB/s';
    return `${this.formatFileSize(bytesPerSecond)}/s`;
  }

  formatTimeRemaining(seconds: number): string {
    if (!seconds || !isFinite(seconds)) return '';
    if (seconds < 60) return `${Math.ceil(seconds)}s left`;
    return `${Math.ceil(seconds / 60)}m left`;
  }

  /** Process upload queue with concurrent limit — same as Task. */
  private processUploadQueue(): void {
    const pending = this.uploadQueue.filter((f) => f.status === 'pending');
    const uploading = this.uploadQueue.filter(
      (f) => f.status === 'uploading',
    ).length;

    const available = this.MAX_CONCURRENT_UPLOADS - uploading;
    const toStart = pending.slice(0, available);

    toStart.forEach((uploadFile) => this.startUpload(uploadFile));
  }

  /** Start individual file upload against this widget's document context. */
  private startUpload(uploadFile: any): void {
    // Same folder resolution the upload dialog uses (see uploadDocument()).
    const folderId =
      this.currentFolder === 0 ? this.rootFolderId : this.currentFolderId;

    if (!folderId) {
      this.handleUploadError(uploadFile, { message: 'Folder not initialized' });
      return;
    }

    uploadFile.status = 'uploading';
    uploadFile.startTime = Date.now();
    this.cdr.detectChanges();

    const uploadParams = {
      module_id: this.module_id,
      folder_id: folderId as number,
      attachable_id: this.record_id ? Number(this.record_id) : undefined,
      attachment_field: 'document',
    };

    const uploadSubscription = this.documentService
      .uploadFileWithProgress(uploadFile.file, uploadParams, false)
      .pipe(
        catchError((error) => {
          this.handleUploadError(uploadFile, error);
          return EMPTY;
        }),
      )
      .subscribe((event: HttpEvent<any>) => {
        this.handleUploadEvent(uploadFile, event);
      });

    this.currentUploads.set(uploadFile.id, uploadSubscription);
  }

  /** Handle upload progress events — same maths as Task. */
  private handleUploadEvent(uploadFile: any, event: HttpEvent<any>): void {
    if (event.type === HttpEventType.UploadProgress && event.total) {
      uploadFile.uploadedBytes = event.loaded;
      uploadFile.totalBytes = event.total;
      uploadFile.progress = Math.round((100 * event.loaded) / event.total);

      const elapsedTime =
        (Date.now() - (uploadFile.startTime || Date.now())) / 1000;
      uploadFile.uploadSpeed = elapsedTime > 0 ? event.loaded / elapsedTime : 0;

      const remainingBytes = event.total - event.loaded;
      uploadFile.timeRemaining =
        uploadFile.uploadSpeed > 0
          ? remainingBytes / uploadFile.uploadSpeed
          : 0;

      this.cdr.detectChanges();
    } else if (event.type === HttpEventType.Response) {
      this.handleUploadComplete(uploadFile, event.body);
    }
  }

  /** Handle successful upload completion — same as Task. */
  private handleUploadComplete(uploadFile: any, response: any): void {
    uploadFile.status = 'complete';
    uploadFile.progress = 100;
    uploadFile.endTime = Date.now();
    uploadFile.uploadedId = response?.data?.id;

    this.currentUploads.delete(uploadFile.id);

    this.toasterService.showSuccess(
      '',
      `${uploadFile.fileName} uploaded successfully`,
    );

    // Paint the completed state first — the UI must not wait on the document
    // list refresh (which is a separate HTTP round-trip).
    this.cdr.detectChanges();

    // Reuse the widget's existing refresh so folder context is respected.
    this.refreshCurrentView();

    this.processUploadQueue();
  }

  /** Handle upload error — same as Task. */
  private handleUploadError(uploadFile: any, error: any): void {
    uploadFile.status = 'error';
    uploadFile.error = error?.message || 'Upload failed';

    this.currentUploads.delete(uploadFile.id);

    this.toasterService.showError(
      '',
      `Failed to upload ${uploadFile.fileName}`,
    );
    this.cdr.detectChanges();

    this.processUploadQueue();
  }

  /** Cancel an in-flight upload. */
  cancelUpload(uploadFile: any): void {
    const subscription = this.currentUploads.get(uploadFile.id);
    if (subscription) {
      subscription.unsubscribe();
      this.currentUploads.delete(uploadFile.id);
    }
    uploadFile.status = 'cancelled';
    this.cdr.detectChanges();
    this.processUploadQueue();
  }

  /** Retry a failed upload. */
  retryUpload(uploadFile: any): void {
    uploadFile.status = 'pending';
    uploadFile.progress = 0;
    uploadFile.error = undefined;
    uploadFile.retryCount = (uploadFile.retryCount || 0) + 1;
    this.processUploadQueue();
  }

  /** Remove a finished/failed row from the queue. */
  removeFromQueue(uploadFile: any): void {
    this.uploadQueue = this.uploadQueue.filter((f) => f.id !== uploadFile.id);
    this.uploadProgress = this.uploadQueue;
    this.cdr.detectChanges();
  }

  getUploadingCount(): number {
    return this.uploadQueue.filter((f) => f.status === 'uploading').length;
  }

  hasCompletedUploads(): boolean {
    return this.uploadQueue.some((f) => f.status === 'complete');
  }

  toggleUploadPanel(): void {
    this.isUploadPanelOpen = !this.isUploadPanelOpen;
  }

  clearCompleted(): void {
    this.uploadQueue = this.uploadQueue.filter((f) => f.status !== 'complete');
    this.uploadProgress = this.uploadQueue;
    this.cdr.detectChanges();
  }

  clearAll(): void {
    this.uploadQueue.forEach((f) => {
      if (f.status === 'uploading') {
        this.currentUploads.get(f.id)?.unsubscribe();
      }
    });
    this.currentUploads.clear();
    this.uploadQueue = [];
    this.uploadProgress = [];
    this.cdr.detectChanges();
  }
  uploadDocument(): void {
    if (!this.canUploadDocument()) {
      return;
    }
    if (this.isLocked) {
      return;
    }
    const uploadData: any = {
      module_id: this.module_id,
      recordId: this.record_id,
      type: 'document',
    };

    // Determine target folder for upload
    if (this.currentFolder === 0) {
      // "All Documents" - upload to root folder
      if (this.rootFolderId) {
        uploadData.folder_id = this.rootFolderId;
      }
    } else if (this.currentFolder > 0 && this.currentFolderId) {
      // Inside specific folder - upload there
      uploadData.folder_id = this.currentFolderId;
    }

    console.log(
      'Upload - currentFolder:',
      this.currentFolder,
      'currentFolderId:',
      this.currentFolderId,
      'rootFolderId:',
      this.rootFolderId,
      'uploadData:',
      uploadData,
    );

    const dialogRef = this.dialogService.open(MediaUploadDialogComponent, {
      data: uploadData,
      width: '800px',
      maxHeight: '80vh',
      panelClass: 'media-upload-dialog-container',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
        // Refresh current view based on folder context
        this.refreshCurrentView();
      }
    });
  }

  openAdvancedFilters(): void {
    // TODO: Implement advanced filter dialog
    console.log('Opening advanced filters');
    // Future implementation for advanced filtering
    this.toasterService.showInfo('Advanced filters coming soon');
  }

  handleDocumentClick(doc: DocumentViewModel): void {
    if (doc.type === 'folder') {
      this.openFolder(doc);
    } else {
      if (!this.canPreviewDocument()) {
        return;
      }
      this.previewDocument(doc);
    }
  }

  /**
   * Setup ResizeObserver to watch viewport size changes
   */
  private setupResizeObserver(): void {
    if (!this.folderViewport || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.calculateScrollDimensions();
    });

    this.resizeObserver.observe(this.folderViewport.nativeElement);
  }

  /**
   * Calculate scroll dimensions based on actual DOM measurements
   */
  /**
   * @returns true when one of the bound scroll fields actually changed, so
   * callers running outside Angular know whether a re-render is warranted.
   * Reading offsetWidth/scrollWidth forces a layout reflow, so this should not
   * be called speculatively on every change detection pass.
   */
  calculateScrollDimensions(): boolean {
    if (!this.folderViewport || !this.folderTabsContainer) {
      return false;
    }

    const viewportElement = this.folderViewport.nativeElement;
    const tabsElement = this.folderTabsContainer.nativeElement;

    // Get actual widths
    const viewportWidth = viewportElement.offsetWidth;
    const totalTabsWidth = tabsElement.scrollWidth;

    const previousMax = this.maxScrollOffset;
    const previousOffset = this.currentScrollOffset;
    const previousLeft = this.canScrollLeftState;
    const previousRight = this.canScrollRightState;

    // Calculate max scroll offset
    this.maxScrollOffset = Math.max(0, totalTabsWidth - viewportWidth);

    // Ensure current scroll is within bounds
    this.currentScrollOffset = Math.min(
      this.currentScrollOffset,
      this.maxScrollOffset,
    );
    this.currentScrollOffset = Math.max(0, this.currentScrollOffset);

    // Update scroll button states
    this.updateScrollButtonStates();

    return (
      this.maxScrollOffset !== previousMax ||
      this.currentScrollOffset !== previousOffset ||
      this.canScrollLeftState !== previousLeft ||
      this.canScrollRightState !== previousRight
    );
  }

  /**
   * Update scroll button enabled/disabled states
   */
  private updateScrollButtonStates(): void {
    this.canScrollLeftState = this.currentScrollOffset > 0;
    this.canScrollRightState = this.currentScrollOffset < this.maxScrollOffset;
  }

  /**
   * Process folders from API response for navigation tabs
   */
  private processFoldersFromResponse(
    documentsArray: any[],
    pagination: any,
  ): void {
    // Filter only folders from the response
    const folderItems = documentsArray.filter(
      (item: any) => item.type === 'folder',
    );

    // Reset folders array with "All Documents" at top
    this.folders = [
      { id: 0, name: 'All Documents', count: pagination.total || 0 },
    ];

    // Add folders from API response
    folderItems.forEach((folder: any) => {
      this.folders.push({
        id: folder.id,
        name: folder.original_name || folder.name || 'Untitled Folder',
        count: folder.children_count || folder.files_count || 0,
      });
    });

    console.log('Folders populated:', this.folders);
  }

  /**
   * Select folder and load its contents
   */
  selectFolder(folderId: number): void {
    this.currentFolder = folderId;

    // Set currentFolderId for upload/create operations
    if (folderId === 0) {
      // "All Documents" - use root folder
      this.currentFolderId = null;
    } else {
      // Specific folder - set currentFolderId to match
      this.currentFolderId = folderId;
    }

    // Reset navigation history and breadcrumbs when clicking any main folder (tabs at top)
    this.navigationHistory = [];
    this.folderPath = [];

    // Auto-scroll to make selected folder visible
    this.scrollToFolder(folderId);

    // Load documents for the selected folder
    this.currentPage = 1;
    this.loadDocuments();
  }

  /**
   * Navigate into a folder (from document list)
   */
  navigateToFolder(folder: DocumentViewModel): void {
    if (folder.type !== 'folder') {
      console.warn('Item is not a folder');
      return;
    }

    console.log('Navigating to folder:', folder.original_name, folder.id);

    // Save current folder to navigation history before navigating
    if (this.currentFolder > 0) {
      const currentFolderName = this.getCurrentFolderName();
      this.navigationHistory.push({
        id: this.currentFolder,
        name: currentFolderName,
      });
    }

    // Add to breadcrumb path
    this.folderPath.push({ id: folder.id, name: folder.original_name });

    // Set current folder and currentFolderId for upload/create operations
    this.currentFolder = folder.id as number;
    this.currentFolderId = folder.id as number;

    this.currentPage = 1;
    this.loadDocuments();
  }

  /**
   * Get the name of the current folder
   */
  private getCurrentFolderName(): string {
    const folder = this.folders.find((f) => f.id === this.currentFolder);
    return folder?.name || 'Folder';
  }

  /**
   * Go back to previous folder using navigation history
   */
  goBackToPreviousFolder(): void {
    if (this.folderPath.length === 0) {
      console.log('No folder path available');
      return;
    }

    // Remove current folder from path
    this.folderPath.pop();

    if (this.folderPath.length > 0) {
      // Go to last folder in path
      const previousFolder = this.folderPath[this.folderPath.length - 1];
      console.log(
        'Going back to folder:',
        previousFolder.name,
        previousFolder.id,
      );

      this.currentFolder = previousFolder.id;
      this.currentFolderId = previousFolder.id;
    } else {
      // Go back to root/All Documents
      console.log('Going back to All Documents');
      this.currentFolder = 0;
      this.currentFolderId = null;
    }

    // Update navigation history to match
    this.navigationHistory = this.folderPath.slice(0, -1);

    this.currentPage = 1;
    this.loadDocuments();
  }

  /**
   * Check if back button should be shown
   */
  shouldShowBackButton(): boolean {
    return this.folderPath.length > 0;
  }

  openFolder(folder: DocumentViewModel): void {
    // Use the new navigation system
    this.navigateToFolder(folder);
  }

  navigateToBreadcrumb(folderId: number | null): void {
    if (folderId === null) {
      // Navigate to root (All Documents)
      this.currentFolder = 0;
      this.currentFolderId = null;
      this.folderPath = [];
      this.navigationHistory = [];
    } else {
      // Navigate to specific folder in path
      const folderIndex = this.folderPath.findIndex((f) => f.id === folderId);
      if (folderIndex !== -1) {
        this.folderPath = this.folderPath.slice(0, folderIndex + 1);
        this.currentFolder = folderId;
        this.currentFolderId = folderId;
        // Update navigation history to match
        this.navigationHistory = this.folderPath.slice(0, -1);
      }
    }
    this.currentPage = 1;
    this.loadDocuments();
  }

  /**
   * Enhanced preview supporting all file types with appropriate viewers
   */
  previewDocument(doc: DocumentViewModel): void {
    if (!this.canPreviewDocument()) {
      return;
    }
    this.openImagePreview(doc);
  }

  /**
   * Open appropriate preview component based on file type
   */
  openImagePreview(model: any): void {
    const fileType = model?.file_type?.toLowerCase();
    const { file_url, ...rest } = model;
    const modifiedModel = { ...rest, url: file_url };

    // Route to appropriate preview component based on file type
    if (this.documentService.allowedImageTypes().includes(fileType)) {
      // IMAGE: jpg, jpeg, png, svg, gif, bmp, webp, tiff — new annotator viewer.
      this.dialog.open(DocumentImageAnnotatorComponent, {
        width: '92vw',
        maxWidth: '92vw',
        height: '88vh',
        data: { ...modifiedModel, __sourceRef: model },
        panelClass: 'document-image-annotator-panel',
      });
    } else if (this.documentService.allowedVideoTypes()?.includes(fileType)) {
      // VIDEO: mp4, mov, mkv, wmv, mpg, mpeg, avi, webm
      this.dialog.open(VideoPreviewComponent, {
        width: '80%',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog',
      });
    } else if (this.documentService.allowedAudioTypes()?.includes(fileType)) {
      // AUDIO: mp3, wav, aac, wma, ogg, m4a
      this.dialog.open(AudioPreviewComponent, {
        width: '600px',
        maxWidth: '90vw',
        data: modifiedModel,
        panelClass: 'media-preview-dialog',
      });
    } else if (fileType === 'pdf') {
      // PDF - Use DocumentPdfPreviewDialogComponent with editing capabilities
      this.lazyPreview.openPdfPreview({
        width: '90vw',
        height: '90vh',
        maxWidth: '1400px',
        maxHeight: '900px',
        data: {
          id: model?.id,
          file_url: model?.file_url,
          original_name: model?.original_name,
          file_type: model?.file_type,
          file_size: model?.file_size,
          creator: model?.creator,
          uploaded_by: model?.uploaded_by,
          created_by: model?.created_by,
        },
        panelClass: 'pdf-preview-dialog-container',
        disableClose: false,
        hasBackdrop: true,
        autoFocus: false,
      });
    } else if (
      fileType === 'csv' ||
      fileType === 'tsv' ||
      fileType === 'xlsx' ||
      fileType === 'xls' ||
      fileType === 'xlsm' ||
      fileType === 'ods'
    ) {
      // SPREADSHEET: csv, tsv, xlsx, xls, xlsm, ods - Use Syncfusion Spreadsheet Editor
      this.lazyPreview.openCsvPreview({
        width: '95vw',
        height: '95vh',
        maxWidth: '95vw',
        maxHeight: '95vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog',
      });
    } else if (
      fileType === 'txt' ||
      fileType === 'log' ||
      fileType === 'json' ||
      fileType === 'xml' ||
      fileType === 'md' ||
      fileType === 'markdown' ||
      fileType === 'yaml' ||
      fileType === 'yml' ||
      fileType === 'html' ||
      fileType === 'htm' ||
      fileType === 'ini' ||
      fileType === 'conf' ||
      fileType === 'rtf' ||
      fileType === 'doc' ||
      fileType === 'docx'
    ) {
      // TEXT/WORD: plain-text/markup + Word docs (doc/docx/rtf) - Use Text/Word Editor
      this.lazyPreview.openTextPreview({
        width: '95vw',
        height: '95vh',
        maxWidth: '95vw',
        maxHeight: '95vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog',
      });
    } else {
      // UNSUPPORTED: no previewer can render this type — show the generic toast.
      // The file is still downloadable via the row's download action.
      this.toasterService.showErrorMessage(UNSUPPORTED_FILE_PREVIEW_MESSAGE);
    }
  }

  downloadDocument(doc: DocumentViewModel): void {
    if (!this.canDownloadDocument()) {
      return;
    }
    const downloadUrl = doc.file_url || doc.url;
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    } else {
      console.warn('No download URL available for document:', doc);
      this.toasterService.showError('Download URL not available');
    }
  }

  shareDocument(doc: DocumentViewModel): void {
    if (!this.canShareDocument()) {
      return;
    }
    // TODO: Implement share functionality
    console.log('Share document:', doc);
    this.toasterService.showInfo('Share functionality coming soon');
  }

  deleteDocument(doc: DocumentViewModel): void {
    if (this.isLocked || !this.canDeleteDocument()) {
      return;
    }
    // Implement delete with confirmation
    const dialogRef = this.dialogService.open(ConfirmationPopupComponent, {
      data: {
        title: 'Delete Document',
        message: `Are you sure you want to delete "${doc.original_name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.documentService.deleteDocument(doc.id).subscribe(
          (res: any) => {
            this.toasterService.showSuccessMessage(res?.data?.message);
            // Refresh the current view (and folder tabs if at root level)
            this.refreshCurrentView();
          },
          (error: any) => {
            console.error('Error deleting document:', error);
            const itemType = doc.type === 'folder' ? 'folder' : 'file';
            this.toasterService.showError(`Failed to delete ${itemType}`);
          },
        );
      }
    });
  }

  // Helper methods
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * File-type icon for a document. Delegates to FileTypeService so the icon in
   * the card title row always matches the one the thumbnail tile renders.
   */
  getDocumentIcon(fileType: string): string {
    return this.fileTypeService.iconForItem({ file_type: fileType });
  }

  /**
   * Check if document is an image
   */
  isImage(doc: DocumentViewModel): boolean {
    return this.fileTypeService.isImage(doc as any);
  }

  /**
   * Check if document is a video
   */
  isVideo(doc: DocumentViewModel): boolean {
    return this.fileTypeService.isVideo(doc as any);
  }

  private getDocumentTypeColor(fileType: string): string {
    const type = fileType?.toLowerCase() || '';

    if (type.includes('pdf')) return 'pdf';
    if (type.includes('doc')) return 'document';
    if (type.includes('xls')) return 'spreadsheet';
    if (type.includes('ppt')) return 'presentation';
    if (type.includes('image') || type.includes('jpg') || type.includes('png'))
      return 'image';

    return 'default';
  }

  private isPreviewable(mimeType: string): boolean {
    const previewableTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
    ];
    return previewableTypes.includes(mimeType);
  }

  private getOwnerAvatar(user: any): string {
    if (!user) return '';
    // Return the actual avatar URL, not initials
    return (
      user.avatar_url ||
      user.avatar ||
      user.user_avatar ||
      user.created_by_avatar ||
      ''
    );
  }

  getDocumentTypeClass(category: string): string {
    return `type-${category}`;
  }

  getStatusBadge(status: string): string {
    const badges: { [key: string]: string } = {
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
      draft: 'Draft',
    };
    return badges[status] || status;
  }

  // Context Menu Methods
  onRightClick(event: MouseEvent, doc: DocumentViewModel): void {
    event.preventDefault();
    event.stopPropagation();

    this.contextMenuItem = doc;
    this.contextMenuX = event.clientX;
    this.contextMenuY = event.clientY;
    this.contextMenuVisible = true;
  }

  closeContextMenu(): void {
    this.contextMenuVisible = false;
    this.contextMenuItem = null;
  }

  handleContextMenuAction(action: string): void {
    if (!this.contextMenuItem) return;

    switch (action) {
      case 'open':
        if (
          this.contextMenuItem.type !== 'folder' &&
          !this.canPreviewDocument()
        ) {
          break;
        }
        if (this.contextMenuItem.type === 'folder') {
          this.openFolder(this.contextMenuItem);
        } else {
          this.previewDocument(this.contextMenuItem);
        }
        break;
      case 'download':
        if (!this.canDownloadDocument()) {
          break;
        }
        if (this.contextMenuItem.type !== 'folder') {
          this.downloadDocument(this.contextMenuItem);
        }
        break;
      case 'rename':
        if (!this.canRenameDocument()) {
          break;
        }
        this.renameItem(this.contextMenuItem);
        break;
      case 'copy':
        this.copyItem(this.contextMenuItem);
        break;
      case 'cut':
        this.cutItem(this.contextMenuItem);
        break;
      case 'paste':
        this.pasteItem();
        break;
      case 'delete':
        if (!this.canDeleteDocument()) {
          break;
        }
        this.deleteDocument(this.contextMenuItem);
        break;
      case 'newFolder':
        if (!this.canCreateFolder()) {
          break;
        }
        this.createNewFolder();
        break;
      case 'uploadHere':
        if (!this.canUploadDocument()) {
          break;
        }
        this.uploadToFolder(this.contextMenuItem);
        break;
      case 'share':
        if (!this.canShareDocument()) {
          break;
        }
        this.shareDocument(this.contextMenuItem);
        break;
      case 'duplicate':
        if (!this.canDuplicateDocument()) {
          break;
        }
        this.duplicateItem(this.contextMenuItem);
        break;
      case 'archive':
        if (!this.canArchiveDocument()) {
          break;
        }
        this.archiveItem(this.contextMenuItem);
        break;
    }

    this.closeContextMenu();
  }

  // Clipboard operations
  copyItem(item: DocumentViewModel): void {
    this.clipboardItems = [item];
    this.clipboardItem = item;
    this.clipboardAction = 'copy';
    this.toasterService.showSuccess('Item copied to clipboard');
  }

  cutItem(item: DocumentViewModel): void {
    this.clipboardItems = [item];
    this.clipboardItem = item;
    this.clipboardAction = 'cut';
    this.toasterService.showSuccess('Item ready to move');
  }

  pasteItem(): void {
    if (this.isLocked) {
      return;
    }
    if (!this.clipboardItems || this.clipboardItems.length === 0) {
      this.toasterService.showWarning('No items in clipboard');
      return;
    }

    // Determine destination folder
    let destinationFolderId: number;

    if (this.currentFolder === 0) {
      // "All Documents" - paste to root folder
      if (!this.rootFolderId) {
        this.toasterService.showWarning('No destination folder available');
        return;
      }
      destinationFolderId = this.rootFolderId;
    } else if (this.currentFolder > 0 && this.currentFolderId) {
      // Inside specific folder - paste there
      destinationFolderId = this.currentFolderId;
    } else {
      this.toasterService.showWarning('No destination folder available');
      return;
    }

    // Use bulk API if multiple items, otherwise use single item API
    if (this.clipboardItems.length > 1) {
      const bulkData = {
        copy_folder_ids: this.clipboardItems.map((item) => Number(item.id)),
        past_folder_id: destinationFolderId,
        action: this.clipboardAction as 'copy' | 'cut',
      };

      console.log('Bulk pasting items:', bulkData);

      this.documentService.bulkPostAction(bulkData).subscribe(
        (response: any) => {
          this.toasterService.showSuccess(
            '',
            `${this.clipboardItems.length} item(s) ${
              this.clipboardAction === 'copy' ? 'copied' : 'moved'
            } successfully`,
          );

          // Clear clipboard after paste
          this.clipboardItems = [];
          this.clipboardItem = null;
          this.clipboardAction = null;

          // Refresh current view
          this.refreshCurrentView();
        },
        (error: any) => {
          console.error('Bulk paste error:', error);
          this.toasterService.showError('Failed to paste items');
        },
      );
    } else {
      // Single item paste (backward compatibility)
      const pasteData = {
        copy_folder_id: this.clipboardItems[0].id,
        past_folder_id: destinationFolderId,
        action: this.clipboardAction,
      };

      console.log('Pasting single item:', pasteData);

      this.documentService.pasteDocument(pasteData).subscribe(
        (response: any) => {
          this.toasterService.showSuccess(
            '',
            `Item ${
              this.clipboardAction === 'copy' ? 'copied' : 'moved'
            } successfully`,
          );

          // Clear clipboard after paste
          this.clipboardItems = [];
          this.clipboardItem = null;
          this.clipboardAction = null;

          // Refresh current view
          this.refreshCurrentView();
        },
        (error: any) => {
          console.error('Paste error:', error);
          this.toasterService.showError('Failed to paste item');
        },
      );
    }
  }

  // Item operations
  renameItem(item: DocumentViewModel): void {
    if (!this.canRenameDocument()) {
      return;
    }
    // TODO: Implement rename dialog
    console.log('Rename item:', item);
    this.toasterService.showInfo('Rename functionality coming soon');
  }

  duplicateItem(item: DocumentViewModel): void {
    if (!this.canDuplicateDocument()) {
      return;
    }
    // TODO: Implement duplicate
    console.log('Duplicate item:', item);
    this.toasterService.showInfo('Duplicate functionality coming soon');
  }

  archiveItem(item: DocumentViewModel): void {
    if (!this.canArchiveDocument()) {
      return;
    }
    // TODO: Implement archive
    console.log('Archive item:', item);
    this.toasterService.showInfo('Archive functionality coming soon');
  }

  createNewFolder(): void {
    if (!this.canCreateFolder()) {
      return;
    }
    if (this.isLocked) {
      return;
    }
    // Determine parent folder for new folder
    let parentFolderId: number;

    if (this.currentFolder === 0) {
      // "All Documents" - create in root folder
      if (!this.rootFolderId) {
        this.toasterService.showWarning(
          'Root folder not ready. Please wait...',
        );
        return;
      }
      parentFolderId = this.rootFolderId;
    } else if (this.currentFolder > 0 && this.currentFolderId) {
      // Inside specific folder - create subfolder there
      parentFolderId = this.currentFolderId;
    } else {
      // Fallback to root folder
      if (!this.rootFolderId) {
        this.toasterService.showWarning(
          'Folder structure not ready. Please wait...',
        );
        return;
      }
      parentFolderId = this.rootFolderId;
    }

    const folderData = {
      module_id: this.module_id,
      folder_id: parentFolderId,
      scope_id: this.record_id,
      type: 'main',
    };

    console.log(
      'Create Folder - currentFolder:',
      this.currentFolder,
      'currentFolderId:',
      this.currentFolderId,
      'rootFolderId:',
      this.rootFolderId,
      'parentFolderId:',
      parentFolderId,
      'folderData:',
      folderData,
    );

    const dialogRef = this.dialogService.open(DocumentCreateFolderComponent, {
      width: '500px',
      maxHeight: '80vh',
      panelClass: 'create-folder-dialog-container',
      data: folderData,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        console.log('Folder created successfully:', result);

        // Refresh the current view to show the new folder
        this.refreshCurrentView();
      }
    });
  }

  uploadToFolder(folder: DocumentViewModel): void {
    // Set the folder as current and trigger upload
    this.currentFolderId = folder.id as number;
    this.uploadDocument();
  }

  private fetchRootFolder(): void {
    console.log('record_id before fetchRootFolder', this.record_id);
    // Don't fetch if we don't have a valid record_id
    if (!this.record_id && !this.module_id) {
      console.log(
        'Skipping root folder fetch - no valid record_id or module_id',
      );
      return;
    }

    // Use getDocuments with auto_parent=true to get breadcrumbs (like document-list-partial)
    const queryParam: any = {
      module_id: this.module_id || 19,
      attachable_id: this.record_id || this.jobId,
      auto_parent: true,
      page: 1,
      per_page: 1,
      loadingService: false,
    };

    this.documentService.getDocuments(queryParam).subscribe(
      (response: any) => {
        console.log('Root folder response:', response);

        // Extract breadcrumbs from response (same as document-list-partial)
        let breadcrumbs = [];
        if (response?.data?.data?.breadcrumbs) {
          breadcrumbs = response.data.data.breadcrumbs;
        } else if (response?.data?.breadcrumbs) {
          breadcrumbs = response.data.breadcrumbs;
        }

        if (breadcrumbs && breadcrumbs.length > 0) {
          // Get the root folder ID from breadcrumbs (last item is current folder)
          const rootFolder = breadcrumbs[breadcrumbs.length - 1];
          this.rootFolderId = rootFolder?.id || null;
          console.log(
            'Root folder ID set from breadcrumbs:',
            this.rootFolderId,
          );

          // Fetch first-level folders to populate folder tabs
          // Only fetch if folders haven't been populated yet (to avoid flicker)
          if (this.folders.length === 1) {
            this.fetchFirstLevelFolders();
          } else {
            console.log(
              'Folders already populated, skipping fetchFirstLevelFolders',
            );
            // Ensure firstLevelFolderIds is set from existing folders
            if (this.firstLevelFolderIds.length === 0) {
              this.firstLevelFolderIds = this.folders
                .filter((f) => f.id !== 0)
                .map((f) => f.id);
              console.log(
                'First-level folder IDs extracted:',
                this.firstLevelFolderIds,
              );
            }
          }
        } else {
          console.warn('No breadcrumbs found, cannot determine root folder');
          this.rootFolderId = null;
        }
      },
      (error: any) => {
        console.error('Error fetching root folder:', error);
        this.rootFolderId = null;
      },
    );
  }

  /**
   * Fetch first-level folders to populate folder tabs
   */
  private fetchFirstLevelFolders(): void {
    if (!this.rootFolderId) {
      console.log(
        'No root folder ID available for fetching first-level folders',
      );
      return;
    }

    const queryParam: any = {
      module_id: this.module_id || 19,
      attachable_id: this.record_id || this.jobId,
      folder_id: this.rootFolderId,
      page: 1,
      per_page: 100, // Get all first-level folders
      include: 'children_count,files_count',
      loadingService: false,
    };

    this.documentService.getDocuments(queryParam).subscribe(
      (response: any) => {
        console.log('First-level folders response:', response);

        // Extract documents array from response
        let documentsArray = [];
        let totalCount = 0;

        if (response?.data?.data?.documents) {
          const documentsData = response.data.data.documents;
          documentsArray = documentsData.list || [];
          totalCount = documentsData.pagination?.total || 0;
        } else if (response?.data && Array.isArray(response.data)) {
          documentsArray = response.data;
          totalCount = response.meta?.total || documentsArray.length;
        }

        // Filter only folders from the response
        const folderItems = documentsArray.filter(
          (item: any) => item.type === 'folder',
        );

        // Store first-level folder IDs for filtering
        this.firstLevelFolderIds = folderItems.map((folder: any) => folder.id);

        // Reset folders array with "All Documents" at top
        // Use this.totalItems if already set by loadDocuments (with all_files=true)
        const allDocsCount = this.totalItems > 0 ? this.totalItems : totalCount;
        this.folders = [{ id: 0, name: 'All Documents', count: allDocsCount }];

        // Add first-level folders to tabs
        folderItems.forEach((folder: any) => {
          this.folders.push({
            id: folder.id,
            name: folder.original_name || folder.name || 'Untitled Folder',
            count: folder.children_count || folder.files_count || 0,
          });
        });

        console.log('Folder tabs populated:', this.folders);
        console.log('First-level folder IDs:', this.firstLevelFolderIds);

        // Recalculate scroll dimensions after folders are updated
        setTimeout(() => {
          this.calculateScrollDimensions();
        }, 100);
      },
      (error: any) => {
        console.error('Error fetching first-level folders:', error);
      },
    );
  }

  /**
   * Refresh the current view after copy/move/delete/create operations
   */
  private refreshCurrentView(): void {
    this.currentPage = 1;

    // Always refresh folder tabs to update counts
    if (this.rootFolderId) {
      this.fetchFirstLevelFolders();
    }

    this.loadDocuments();
  }

  /**
   * Check if actions should be enabled (for one or more selections)
   */
  hasItemsSelected(): boolean {
    return this.selectedDocuments.length > 0;
  }

  /**
   * Check if paste action should be enabled
   */
  canPaste(): boolean {
    return this.clipboardItems.length > 0 || this.clipboardItem !== null;
  }

  /**
   * Get the first selected document
   */
  getSelectedDocument(): DocumentViewModel | undefined {
    if (this.selectedDocuments.length !== 1) {
      return undefined;
    }
    return this.documents.find((d) => this.isDocumentSelected(d.id.toString()));
  }

  /**
   * Get clipboard item count for display
   */
  getClipboardItemCount(): number {
    return this.clipboardItems.length;
  }

  /**
   * Get clipboard display text
   */
  getClipboardDisplayText(): string {
    if (this.clipboardItems.length === 0) return '';
    if (this.clipboardItems.length === 1) {
      const name = this.clipboardItems[0].original_name;
      return name.length > 10 ? name.substring(0, 10) + '...' : name;
    }
    return `${this.clipboardItems.length} items`;
  }

  /**
   * Handle copy action for selected documents
   */
  handleCopySelected(): void {
    if (this.isLocked) {
      return;
    }
    const selectedDocs = this.documents.filter((d) =>
      this.selectedDocuments.includes(d.id.toString()),
    );
    if (selectedDocs.length === 0) {
      return;
    }

    this.clipboardItems = [...selectedDocs];
    this.clipboardAction = 'copy';

    // For backward compatibility, also set single item
    this.clipboardItem = selectedDocs[0];

    this.toasterService.showSuccess(
      '',
      `${selectedDocs.length} item(s) copied to clipboard`,
    );
    this.clearSelection();
  }

  /**
   * Handle cut action for selected documents
   */
  handleCutSelected(): void {
    if (this.isLocked) {
      return;
    }
    const selectedDocs = this.documents.filter((d) =>
      this.selectedDocuments.includes(d.id.toString()),
    );
    if (selectedDocs.length === 0) {
      return;
    }

    this.clipboardItems = [...selectedDocs];
    this.clipboardAction = 'cut';

    // For backward compatibility, also set single item
    this.clipboardItem = selectedDocs[0];

    this.toasterService.showSuccess(
      '',
      `${selectedDocs.length} item(s) ready to move`,
    );
    this.clearSelection();
  }

  /**
   * Get avatar color based on name (for consistent user colors)
   */
  getAvatarColor(name: string): string {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    ];

    // Simple hash function to get consistent color for same name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % colors.length);
    return colors[index];
  }

  /**
   * Get initials from name
   */
  getInitials(name: string): string {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  // Folder scrolling methods - NEW IMPLEMENTATION
  canScrollLeft(): boolean {
    return this.canScrollLeftState;
  }

  canScrollRight(): boolean {
    return this.canScrollRightState;
  }

  scrollFoldersLeft(): void {
    if (this.canScrollLeftState) {
      // Scroll left by average folder width
      this.currentScrollOffset = Math.max(
        0,
        this.currentScrollOffset - this.averageFolderWidth,
      );
      this.updateScrollButtonStates();
    }
  }

  scrollFoldersRight(): void {
    if (this.canScrollRightState) {
      // Scroll right by average folder width
      this.currentScrollOffset = Math.min(
        this.maxScrollOffset,
        this.currentScrollOffset + this.averageFolderWidth,
      );
      this.updateScrollButtonStates();
    }
  }

  /**
   * Get transform style for folder tabs scrolling
   */
  getFolderTabsTransform(): string {
    return `translateX(-${this.currentScrollOffset}px)`;
  }

  /**
   * Scroll to make a specific folder visible in the viewport
   */
  private scrollToFolder(folderId: number): void {
    if (!this.folderViewport || !this.folderTabsContainer) {
      return;
    }

    // Find the folder index
    const folderIndex = this.folders.findIndex((f) => f.id === folderId);
    if (folderIndex === -1) {
      return;
    }

    // Get all folder tab elements
    const tabsElement = this.folderTabsContainer.nativeElement;
    const folderElements = tabsElement.querySelectorAll('.folder-tab');

    if (folderElements.length <= folderIndex) {
      return;
    }

    // Get the target folder element
    const targetFolder = folderElements[folderIndex] as HTMLElement;
    const viewportElement = this.folderViewport.nativeElement;

    // Calculate positions
    const viewportWidth = viewportElement.offsetWidth;
    const folderLeft = targetFolder.offsetLeft;
    const folderWidth = targetFolder.offsetWidth;

    // Check if folder is already visible
    const visibleLeft = this.currentScrollOffset;
    const visibleRight = this.currentScrollOffset + viewportWidth;

    // If folder is not fully visible, scroll to it
    if (folderLeft < visibleLeft) {
      // Folder is to the left of viewport - scroll left to show it
      this.currentScrollOffset = folderLeft;
    } else if (folderLeft + folderWidth > visibleRight) {
      // Folder is to the right of viewport - scroll right to show it
      this.currentScrollOffset = Math.min(
        this.maxScrollOffset,
        folderLeft + folderWidth - viewportWidth,
      );
    }

    // Update button states
    this.updateScrollButtonStates();
  }

  // Check if mobile view
  private checkMobileView(): void {
    const width = window.innerWidth;
    this.isMobileView = width < 768;
  }
}
